import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Globe2,
  Loader2,
  Plus,
  RotateCw,
  Send,
  Wallet as WalletIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { matchesCountrySearch } from "@/lib/countries";
import { publicApi } from "@/services/api-client";
import { transactionFrom } from "@/features/dashboard/dashboard-context";
import {
  OP_LABEL,
  countryOf,
  fmt,
  fmtDate,
  signed,
  type Tx,
} from "@/types/dashboard";
import {
  EnvironmentBadge,
  Flag,
  KybLock,
  PageHead,
  Panel,
  StatusPill,
} from "@/components/bits";
import {
  PaymentDialog,
  type PaymentKind,
} from "@/features/dashboard/payment-dialog";
import { TxDetailDialog } from "@/components/tx-detail-dialog";
import { useI18n } from "@/i18n/i18n-context";
import {
  ErrorBox,
  KV,
  Pager,
  SkeletonRows,
  Tag,
  buildQuery,
  fmtAmount,
  pageInfo,
  pick,
  record,
  rows,
  text,
  useFinanceGate,
  useServer,
  type ApiRecord,
} from "@/features/finance/kit";

const PER_PAGE = 25;
const TYPES = ["MAIN", "SAVINGS", "ESCROW", "RESERVE"] as const;

function walletView(w: ApiRecord) {
  const accounts = Array.isArray(w.accounts) ? (w.accounts as ApiRecord[]) : [];
  const availableAcc = accounts.find(
    (a) => a && String(a.type).toUpperCase() === "AVAILABLE",
  );

  return {
    id: text(w, "id"),
    country:
      text(w, "country_code", "country.code", "country.alpha2_code") ||
      (text(w, "scope") === "INTERNATIONAL" ? "INT" : "CM"),
    currency:
      text(w, "currency_code", "currency.code") ||
      (typeof w.currency === "string" ? w.currency : "XAF"),
    balance:
      pick(w, "available_balance", "balance", "balances.available") ??
      (availableAcc?.balance !== undefined ? Number(availableAcc.balance) : 0),
    status: text(w, "status") || "ACTIVE",
    type: text(w, "type", "wallet_type") || "MAIN",
    scope: text(w, "scope") || "LOCAL",
  };
}

/** Catalog entry: currency ID must come from the server, never invented. */
function catalogView(c: ApiRecord) {
  const code = text(c, "code");
  return {
    id: text(c, "id"),
    code,
    alpha2: text(c, "alpha2_code"),
    name: text(c, "name") || code,
    active: pick(c, "is_active", "active") !== false,
    currencyId: text(c, "default_currency_id", "default_currency.id"),
    currencyCode:
      text(c, "default_currency.code", "default_currency_code") ||
      (typeof c.default_currency === "string" ? c.default_currency : ""),
    access:
      text(
        c,
        "access_status",
        "access.status",
        "account_access.status",
        "current_access.status",
      ) || "NONE",
    walletExists: pick(c, "wallet_exists", "has_wallet") === true,
    existingWalletId: text(c, "existing_wallet_id", "wallet.id"),
    isInt: code === "INT",
  };
}

export default function Wallets() {
  const gate = useFinanceGate();
  const d = gate.d;
  const params = new URLSearchParams(useSearch());
  const [, nav] = useLocation();
  const { t, lang, dict } = useI18n();
  const [currency, setCurrency] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [sim, setSim] = useState<PaymentKind | null>(null);
  const [detail, setDetail] = useState<Tx | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  useEffect(() => setPage(1), [currency, status]);

  const list = useServer<unknown>(
    gate.allowed
      ? buildQuery("/wallets", { currency, status, per_page: PER_PAGE, page })
      : null,
  );
  const raw = useMemo(() => rows(list.data), [list.data]);
  const wallets = useMemo(() => raw.map(walletView), [raw]);
  const info = pageInfo(list.data, list.meta, raw.length, PER_PAGE);
  const selectedId = params.get("w") ?? wallets[0]?.id ?? null;
  const detailRes = useServer<unknown>(
    gate.allowed && selectedId
      ? `/wallets/${encodeURIComponent(selectedId)}`
      : null,
  );
  const selRaw = record(record(detailRes.data).wallet ?? detailRes.data);
  const selected = detailRes.data
    ? walletView(selRaw)
    : wallets.find((w) => w.id === selectedId);
  const recentTransactions = record(detailRes.data).recent_transactions;
  const wtx = Array.isArray(recentTransactions)
    ? recentTransactions.map((transaction) =>
        transactionFrom(record(transaction)),
      )
    : selected
      ? d.transactions.filter((t) => t.walletId === selected.id).slice(0, 3)
      : [];
  const currencies = Array.from(
    new Set(d.wallets.map((w) => w.currency).filter(Boolean)),
  );

  if (gate.locked || (!gate.loading && !gate.allowed))
    return (
      <>
        <PageHead title="Wallets" />
        <KybLock what="Les wallets pays" />
      </>
    );

  return (
    <>
      <PageHead title={t("wallets.title")} sub={t("wallets.sub")}>
        <EnvironmentBadge />
        <Button
          variant="outline"
          onClick={() => setCatalogOpen(true)}
          data-testid="button-open-catalog"
          className="gap-1.5"
        >
          <Globe2 className="h-4 w-4" /> {t("wallets.countriesAndNewWallet")}
        </Button>
      </PageHead>
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger
            className="w-40 bg-card"
            data-testid="filter-wallet-currency"
            aria-label={t("wallets.filterCurrency")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("wallets.allCurrencies")}</SelectItem>
            {(currencies.length
              ? currencies
              : ["XAF", "XOF", "CDF", "USD"]
            ).map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger
            className="w-40 bg-card"
            data-testid="filter-wallet-status"
            aria-label={t("wallets.filterStatus")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("wallets.allStatuses")}</SelectItem>
            {["ACTIVE", "PENDING", "SUSPENDED", "CLOSED"].map((s) => (
              <SelectItem key={s} value={s}>
                {(dict.statuses as Record<string, string>)[s] ?? s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          className="h-9"
          onClick={list.reload}
          aria-label={t("wallets.refreshWallets")}
        >
          <RotateCw className={cn("h-4 w-4", list.loading && "animate-spin")} />
        </Button>
      </div>
      <div className="grid gap-5 lg:grid-cols-[360px_1fr]">
        <Panel className="overflow-hidden p-0">
          {list.error ? (
            <ErrorBox
              className="m-3"
              error={list.error}
              onRetry={list.reload}
            />
          ) : list.loading && !wallets.length ? (
            <SkeletonRows n={4} />
          ) : wallets.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-muted-foreground">
              <WalletIcon className="h-6 w-6" />
              {t("wallets.emptyWallets")}
            </div>
          ) : (
            <ul className="space-y-1 p-2">
              {wallets.map((w) => (
                <li key={w.id}>
                  <button
                    data-testid={`wallet-row-${w.country}`}
                    onClick={() => nav(`/wallets?w=${w.id}`)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition",
                      selected?.id === w.id
                        ? "bg-accent ring-1 ring-primary/20"
                        : "hover:bg-accent/50",
                    )}
                  >
                    <Flag code={w.country} />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">
                        {countryOf(w.country).name}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {w.type} · {w.scope}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="tnum text-sm font-bold">
                        {fmtAmount(w.balance, w.currency)}
                      </p>
                      {w.status !== "ACTIVE" && (
                        <p className="text-[10px] font-semibold text-amber-700">
                          {w.status}
                        </p>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {(wallets.length > 0 || page > 1) && (
            <Pager info={info} loading={list.loading} onPage={setPage} />
          )}
        </Panel>
        {selected && (
          <Panel>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <Flag code={selected.country} />
                  {countryOf(selected.country).name} · {selected.currency}
                </p>
                <p
                  data-testid="text-wallet-balance"
                  className="tnum mt-2 text-4xl font-extrabold tracking-tight"
                >
                  {fmtAmount(selected.balance, selected.currency)}
                </p>
                <p className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  Solde disponible <Tag value={selected.status} />
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={selected.status !== "ACTIVE"}
                  onClick={() => setSim("DEPOSIT")}
                  data-testid="button-wallet-deposit"
                >
                  <ArrowDownToLine className="mr-1 h-4 w-4" />{" "}
                  {t("wallets.deposit")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={selected.status !== "ACTIVE"}
                  onClick={() => setSim("WITHDRAWAL")}
                  data-testid="button-wallet-withdraw"
                >
                  <ArrowUpFromLine className="mr-1 h-4 w-4" />{" "}
                  {t("wallets.withdraw")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={selected.status !== "ACTIVE"}
                  onClick={() => setSim("TRANSFER")}
                  data-testid="button-wallet-transfer"
                >
                  <Send className="mr-1 h-4 w-4" /> {t("wallets.transfer")}
                </Button>
              </div>
            </div>
            {selected.status === "PENDING" && (
              <p className="mt-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
                {lang === "en"
                  ? "Country access for this wallet is pending administrative review. Operations are unavailable."
                  : "L'accès pays pour ce wallet attend une validation administrative. Les opérations sont indisponibles."}
              </p>
            )}
            {detailRes.error ? (
              <ErrorBox
                className="mt-4"
                error={detailRes.error}
                onRetry={detailRes.reload}
              />
            ) : null}
            <h3 className="mb-2 mt-6 text-sm font-bold">
              {t("wallets.transactionsForWallet")} ({wtx.length})
            </h3>
            {wtx.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t("wallets.emptyWalletTxs")}
              </p>
            ) : (
              <ul className="divide-y">
                {wtx.slice(0, 3).map((t) => (
                  <li key={t.reference}>
                    <button
                      onClick={() => setDetail(t)}
                      className="flex w-full items-center gap-3 rounded-md px-1 py-2.5 text-left transition hover:bg-accent/40"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold">
                          {OP_LABEL[t.operation]} · {t.counterparty}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {fmtDate(t.createdAt)}
                        </p>
                      </div>
                      <StatusPill status={t.status} />
                      <span className="tnum w-32 text-right text-sm font-bold">
                        {signed(t) < 0 ? "−" : "+"}
                        {fmt(t.amount, t.currency)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        )}
      </div>
      <PaymentDialog
        kind={sim}
        onClose={() => setSim(null)}
        defaultWalletId={selected?.id}
      />
      <TxDetailDialog tx={detail} onClose={() => setDetail(null)} />
      <CatalogDialog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        onWalletCreated={list.reload}
      />
    </>
  );
}

function CatalogDialog({
  open,
  onClose,
  onWalletCreated,
}: {
  open: boolean;
  onClose: () => void;
  onWalletCreated: () => void;
}) {
  const { lang } = useI18n();
  const catalog = useServer<unknown>(open ? "/country-access/catalog" : null);
  const entries = useMemo(
    () => rows(catalog.data).map(catalogView),
    [catalog.data],
  );
  const countryPolicy = record(catalog.meta?.country_access_policy);
  const countryLimitReached = countryPolicy.can_add_country === false;
  const [countrySearch, setCountrySearch] = useState("");
  const filteredEntries = entries.filter((e) =>
    matchesCountrySearch(
      { name: `${e.name} ${e.alpha2} ${e.currencyCode}`, code: e.code },
      countrySearch,
    ),
  );
  const [mode, setMode] = useState<{
    kind: "access" | "wallet";
    id: string;
  } | null>(null);
  const entry = entries.find((e) => e.id === mode?.id);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>Accès pays et wallets</DialogTitle>
          <DialogDescription>
            Catalogue officiel. La devise de chaque wallet est imposée par le
            pays ; elle ne peut pas être remplacée.
          </DialogDescription>
          {!mode && (
            <div className="rounded-lg border border-blue-100 bg-blue-50/70 p-3 text-sm text-blue-950">
              <p className="font-semibold">
                Niveau{" "}
                {text(countryPolicy, "verification_tier") || "UNVERIFIED"}
              </p>
              <p className="mt-1 text-xs">
                {countryPolicy.api_access_available === false ? (
                  lang === "en" ? (
                    <>
                      No country or wallet-count limit. Only enabled countries
                      and services are available. API access is reserved for
                      BUSINESS accounts.
                    </>
                  ) : (
                    <>
                      Aucune limite sur le nombre de pays ou de wallets. Seuls
                      les pays et services activés sont disponibles. L’accès API
                      reste réservé aux comptes BUSINESS.
                    </>
                  )
                ) : (
                  <>
                    {countryPolicy.max_countries == null
                      ? lang === "en"
                        ? "Access to all available countries."
                        : "Accès à tous les pays disponibles."
                      : lang === "en"
                        ? `${text(countryPolicy, "selected_country_count") || "0"} / ${text(countryPolicy, "max_countries")} countries used · ${text(countryPolicy, "remaining_country_slots") || "0"} remaining.`
                        : `${text(countryPolicy, "selected_country_count") || "0"} / ${text(countryPolicy, "max_countries")} pays utilisés · ${text(countryPolicy, "remaining_country_slots") || "0"} restant(s).`}{" "}
                    {lang === "en"
                      ? "Payins and payouts share a daily limit per country in its local currency. Payment-link payins are included; virtual-card operations are excluded."
                      : "Les payins et payouts partagent un plafond journalier par pays, dans sa devise locale. Les payins par lien sont inclus ; les opérations de carte virtuelle sont exclues."}
                  </>
                )}
              </p>
            </div>
          )}
        </DialogHeader>
        {!mode && (
          <Input
            type="search"
            aria-label="Rechercher dans le catalogue des pays"
            placeholder="Rechercher un pays, un code ou une monnaie…"
            value={countrySearch}
            onChange={(event) => setCountrySearch(event.target.value)}
          />
        )}
        {mode && entry ? (
          mode.kind === "access" ? (
            <AccessForm
              entry={entry}
              onDone={() => {
                setMode(null);
                catalog.reload();
              }}
              onBack={() => setMode(null)}
            />
          ) : (
            <WalletForm
              entry={entry}
              onDone={() => {
                setMode(null);
                onWalletCreated();
                catalog.reload();
              }}
              onBack={() => setMode(null)}
            />
          )
        ) : catalog.error ? (
          <ErrorBox error={catalog.error} onRetry={catalog.reload} />
        ) : catalog.loading ? (
          <SkeletonRows n={5} />
        ) : entries.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Le catalogue ne contient aucun pays.
          </p>
        ) : (
          <ul
            className="divide-y rounded-lg border"
            data-testid="list-country-catalog"
          >
            {filteredEntries.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                Aucun pays ne correspond à votre recherche.
              </li>
            )}
            {filteredEntries.map((e) => (
              <li
                key={e.id || e.code}
                className="flex flex-wrap items-center gap-3 px-3 py-2.5"
              >
                <Flag code={e.isInt ? "INT" : e.alpha2} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">
                    {e.name}{" "}
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {e.code}
                    </span>
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Devise : {e.currencyCode || "non fournie"}
                    {!e.active ? " · pays inactif" : ""}
                  </p>
                </div>
                <Tag value={e.access} />
                <div className="flex gap-1.5">
                  {e.access.toUpperCase() !== "ACTIVE" &&
                    e.access.toUpperCase() !== "PENDING" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        disabled={!e.id || !e.active || countryLimitReached}
                        onClick={() => setMode({ kind: "access", id: e.id })}
                        data-testid={`button-request-access-${e.code}`}
                      >
                        {countryLimitReached
                          ? "Limite de pays atteinte"
                          : "Demander l'accès"}
                      </Button>
                    )}
                  <Button
                    size="sm"
                    className="h-8"
                    disabled={!e.id || !e.active || e.walletExists}
                    onClick={() => setMode({ kind: "wallet", id: e.id })}
                    data-testid={`button-create-wallet-${e.code}`}
                    title={
                      e.walletExists
                        ? "Un wallet existe déjà pour ce pays."
                        : undefined
                    }
                  >
                    {e.walletExists ? (
                      <>
                        <WalletIcon className="mr-1 h-3.5 w-3.5" /> Déjà créé
                      </>
                    ) : (
                      <>
                        <Plus className="mr-1 h-3.5 w-3.5" /> Wallet
                      </>
                    )}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

type Entry = ReturnType<typeof catalogView>;

function AccessForm({
  entry,
  onDone,
  onBack,
}: {
  entry: Entry;
  onDone: () => void;
  onBack: () => void;
}) {
  const [flags, setFlags] = useState({
    collection_enabled: true,
    distribution_enabled: true,
    wallet_creation_enabled: true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<ApiRecord | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const { data } = await publicApi<ApiRecord>(
        "/account-country-access/requests",
        {
          method: "POST",
          body: { country_id: entry.id, ...flags },
        },
      );
      setResult(record(data));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  if (result)
    return (
      <div className="space-y-3" data-testid="access-request-result">
        <p className="text-sm font-bold">
          Demande enregistrée{" "}
          <Tag value={text(result, "status", "request.status")} />
        </p>
        <p className="text-xs text-muted-foreground">
          L’accès est activé immédiatement lorsqu’il est autorisé. Un
          administrateur peut ensuite restreindre les opérations par pays.
        </p>
        <KV data={result} />
        <Button onClick={onDone}>Retour au catalogue</Button>
      </div>
    );
  return (
    <div className="space-y-4">
      <p className="text-sm font-semibold">Demande d'accès : {entry.name}</p>
      {(
        [
          ["collection_enabled", "Encaissements"],
          ["distribution_enabled", "Décaissements"],
          ["wallet_creation_enabled", "Création de wallet"],
        ] as const
      ).map(([k, l]) => (
        <label
          key={k}
          className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm"
        >
          {l}
          <Switch
            checked={flags[k]}
            onCheckedChange={(v) => setFlags((f) => ({ ...f, [k]: v }))}
            aria-label={l}
          />
        </label>
      ))}
      <ErrorBox error={error} onRetry={() => void submit()} />
      <DialogFooter>
        <Button variant="outline" onClick={onBack} disabled={busy}>
          Retour
        </Button>
        <Button
          onClick={() => void submit()}
          disabled={busy}
          data-testid="button-submit-access"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Envoyer la
          demande
        </Button>
      </DialogFooter>
    </div>
  );
}

function WalletForm({
  entry,
  onDone,
  onBack,
}: {
  entry: Entry;
  onDone: () => void;
  onBack: () => void;
}) {
  const [type, setType] = useState<(typeof TYPES)[number]>("MAIN");
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<ApiRecord | null>(null);
  const scope = entry.isInt ? "INTERNATIONAL" : "LOCAL";
  const intMismatch =
    entry.isInt && entry.currencyCode && entry.currencyCode !== "USD";

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const { data } = await publicApi<ApiRecord>("/wallets", {
        method: "POST",
        body: {
          country_id: entry.id,
          currency_id: entry.currencyId,
          scope,
          type,
          is_default: isDefault,
        },
      });
      setResult(record(data));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  if (result)
    return (
      <div className="space-y-3" data-testid="wallet-created">
        <p className="text-sm font-bold">
          Wallet créé par le serveur{" "}
          <Tag value={text(result, "status", "wallet.status")} />
        </p>
        <KV data={result} />
        <Button onClick={onDone}>Terminer</Button>
      </div>
    );
  return (
    <div className="space-y-4">
      <p className="text-sm font-semibold">Nouveau wallet : {entry.name}</p>
      {!entry.currencyId ? (
        <p
          role="alert"
          className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900"
          data-testid="wallet-currency-missing"
        >
          Le catalogue ne fournit pas l'identifiant de devise par défaut pour ce
          pays. La création est bloquée : BizConnectPay ne choisit jamais une
          devise à la place du serveur.
        </p>
      ) : (
        <dl className="grid grid-cols-2 gap-2 rounded-lg bg-muted/50 p-3 text-xs">
          <dt className="text-muted-foreground">Portée</dt>
          <dd className="text-right font-semibold">{scope}</dd>
          <dt className="text-muted-foreground">Devise imposée</dt>
          <dd className="text-right font-mono font-semibold">
            {entry.currencyCode || entry.currencyId}
          </dd>
        </dl>
      )}
      {intMismatch && (
        <p className="text-xs text-destructive">
          Le wallet International doit être en USD ; la réponse du catalogue est
          incohérente.
        </p>
      )}
      <div className="grid gap-1.5">
        <Label htmlFor="wallet-type">Type</Label>
        <Select
          value={type}
          onValueChange={(v) => setType(v as (typeof TYPES)[number])}
        >
          <SelectTrigger id="wallet-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <label className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
        Wallet par défaut pour ce pays
        <Switch
          checked={isDefault}
          onCheckedChange={setIsDefault}
          aria-label="Wallet par défaut"
        />
      </label>
      <ErrorBox error={error} onRetry={() => void submit()} />
      <DialogFooter>
        <Button variant="outline" onClick={onBack} disabled={busy}>
          Retour
        </Button>
        <Button
          onClick={() => void submit()}
          disabled={busy || !entry.currencyId || Boolean(intMismatch)}
          data-testid="button-submit-wallet"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Créer le
          wallet
        </Button>
      </DialogFooter>
    </div>
  );
}
